import React from 'react';

import {WarehouseRelationshipData} from 'insights/warehouse/warehouseModelsDataLayout';
import {BLUE_DARKER, BLUE_LIGHT} from 'ui-library/Colors';

import {DiagramPixelPoint, WarehouseRelationshipPath} from './diagram-layout/diagramLayoutTypes';
import {useDiagramLayoutContext} from './DiagramLayoutContext';
import {useDiagramPanningContext} from './DiagramPanningContext';
import {getRelationshipDecoratorPixelPath, getRelationshipDecoratorTypes} from './relationshipPathDecorator';

/**
 * Convert list of points to path string for SVG path.
 *
 * Example: [{x: 1, y: 2}, {x: 3, y: -1}] -> "1,2 3,-1"
 */
export function stringifyPath(path: Array<DiagramPixelPoint>): string {
  return path.map(({x, y}) => `${x},${y}`).join(' ');
}

export interface WarehouseDiagramRelationshipProps {
  hovered: boolean;
  relationshipData: WarehouseRelationshipData;
  path: WarehouseRelationshipPath;
}

export default function WarehouseDiagramRelationship(props: WarehouseDiagramRelationshipProps): JSX.Element {
  const {points, sourceExitSide, targetEntranceSide} = props.path;
  const isPanning = useDiagramPanningContext();
  const {startHoverRelationship, hideRelationshipTooltip, showRelationshipTooltip} =
    useDiagramLayoutContext();

  function handleRelationshipHover(): void {
    if (props.hovered || isPanning) {
      // Re-hovering over this relationship (might happen when moving from hovering the path
      // to one of the relationships' decorators) should not lead to startHoverRelationship being called.
      // The check to isPanning is to prevent hovering relationships while panning around the diagram.
      return;
    }

    startHoverRelationship(props.relationshipData);
  }

  const stringifiedMainPath = stringifyPath(points);

  const {sourceDecoratorType, targetDecoratorType} = getRelationshipDecoratorTypes(
    props.relationshipData.type
  );
  const sourceDecorator = getRelationshipDecoratorPixelPath(sourceDecoratorType, sourceExitSide, points[0]);
  const targetDecorator = getRelationshipDecoratorPixelPath(
    targetDecoratorType,
    targetEntranceSide,
    points[points.length - 1]
  );

  return (
    <g
      // Use the default cursor when panning because relationship hover events are disabled when panning.
      cursor={isPanning ? 'unset' : 'pointer'}
      pointerEvents="stroke"
      // Define mouse events on the group so that they are applied to all children.
      onMouseOver={handleRelationshipHover}
      onMouseMove={(e) => {
        // Prevents mousemove while hovering a relationship from propagating to the diagram wrapper,
        // which would then call stopHoverRelationship.
        if (!isPanning) {
          // If panning, allow mousemove events to propagate so as to not interrupt the panning experience.
          e.stopPropagation();
        }
      }}
      onMouseDown={(e) => {
        // Close the open tooltip (if one is open) so that the tooltip gets re-rendered with the new
        // relationship.
        hideRelationshipTooltip();
        // Prevents panning the diagram by clicking & dragging a relationship path.
        e.stopPropagation();
      }}
      onClick={(e) => {
        showRelationshipTooltip(props.relationshipData, {x: e.clientX, y: e.clientY});
      }}
    >
      <polyline // Transparent & thicker version of the relationship path to make hovering easier
        points={stringifiedMainPath}
        fill="none"
        stroke="none"
        strokeWidth="10"
        key={1}
      />
      <polyline // Displayed relationship path
        points={stringifiedMainPath}
        fill="none"
        stroke={props.hovered ? BLUE_DARKER : BLUE_LIGHT}
        strokeWidth={props.hovered ? 3 : 2}
        strokeLinejoin="round"
        key={2}
      />
      <polyline // Source relationship decorator
        stroke={props.hovered ? BLUE_DARKER : BLUE_LIGHT}
        strokeWidth={props.hovered ? 3 : 2}
        fill="transparent"
        points={stringifyPath(sourceDecorator)}
        key={3}
      />
      <polyline // Target relationship decorator
        stroke={props.hovered ? BLUE_DARKER : BLUE_LIGHT}
        strokeWidth={props.hovered ? 3 : 2}
        fill="transparent"
        points={stringifyPath(targetDecorator)}
        key={4}
      />
    </g>
  );
}
